import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import type { Tarjima } from "../utils/i18n";
import { DAY_FILL_COLORS, type DayCell, type DayFill } from "../utils/summary";

/** Kechikish — YUQORI chetdagi chiziqcha; erta ketish — PASTKI. */
const LATE_COLOR = "#fab219";
const EARLY_COLOR = "#ec835a";

/**
 * `DayFill` → to'liq o'qish uchun matn (`employees.json`ning
 * `dayStrip.fillFull.*` kalitlaridan).
 */
function dayFillLabel(t: Tarjima, fill: DayFill): string {
	return t(`dayStrip.fillFull.${fill}`);
}

function izoh(t: Tarjima, cell: DayCell): string {
	const qismlar = [cell.date, dayFillLabel(t, cell.fill)];

	if (cell.late) {
		qismlar.push(t("dayStrip.late"));
	}
	if (cell.early) {
		qismlar.push(t("dayStrip.early"));
	}

	return qismlar.join(" · ");
}

/**
 * Davomat lentasi (dizayn kanvasi, «Xodim profili»).
 *
 * ⚠️ **`windowDays` PROP, qattiq kodlangan 30 EMAS.** Oyna tanlagichi
 * 90 kunga o'tganda `aria-label` da qolgan «30» ekran o'quvchisi uchun
 * ochiq yolg'on bo'lardi — ko'z lentaning uzayganini ko'radi, quloq esa
 * yo'q.
 *
 * ⚠️ **Kechikish va erta ketish RANG bilan berilmaydi** — `#fab219` va
 * `#ec835a` oddiy ko'rishda ham ΔE 13,6 bilan ajralmaydi. Ular
 * katakning yuqori va pastki chetidagi 2 px chiziqcha: ikki ma'no bir
 * katakda birga ko'rinishi ham SHU sababdan mumkin.
 *
 * ⚠️ **Rang YOLG'IZ ma'no tashuvchi emas** — har katakning `title`
 * (tooltip) matni holatni SO'Z bilan ham aytadi, aks holda rangni
 * ajrata olmaydigan foydalanuvchi uchun lenta ma'nosiz bo'lardi.
 *
 * ⚠️ **`@/shared/charts` dagi `DayStrip` bilan NOMDOSH, lekin BOSHQA
 * komponent** — u rang + matnni tashqaridan oluvchi umumiy primitiv.
 * Import yo'lini tekshiring. Birlashtirish — ochiq qarz.
 */
export function DayStrip({ cells, windowDays }: { cells: DayCell[]; windowDays: number }) {
	const { t } = useTranslation("employees");

	return (
		// ⚠️ `<ul>`/`<li>` — `role="list"` bo'lgan `<div>` ni Biome ning
		// `a11y/useSemanticElements` qoidasi rad etadi.
		<ul
			className="flex flex-wrap gap-1 m-0 p-0 list-none"
			aria-label={t("dayStrip.ariaLabel", { soni: windowDays })}
		>
			{cells.map((cell) => (
				<Tooltip key={cell.date} title={izoh(t, cell)}>
					<li
						data-testid="day-cell"
						aria-label={izoh(t, cell)}
						className="relative h-7 w-4 rounded-sm"
						style={{ backgroundColor: DAY_FILL_COLORS[cell.fill] }}
					>
						{cell.late ? (
							<span
								className="absolute inset-x-0 top-0 h-[2px] rounded-t-sm"
								style={{ backgroundColor: LATE_COLOR }}
							/>
						) : null}
						{cell.early ? (
							<span
								className="absolute inset-x-0 bottom-0 h-[2px] rounded-b-sm"
								style={{ backgroundColor: EARLY_COLOR }}
							/>
						) : null}
					</li>
				</Tooltip>
			))}
		</ul>
	);
}

/** Lenta ostidagi izoh — ranglarni so'z bilan bog'laydi. */
export function DayStripLegend() {
	const { t } = useTranslation("employees");

	return (
		<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#565e6b]">
			{(Object.keys(DAY_FILL_COLORS) as Array<keyof typeof DAY_FILL_COLORS>).map((fill) => (
				<span key={fill} className="inline-flex items-center gap-1.5">
					<span
						className="inline-block h-2.5 w-2.5 rounded-sm"
						style={{ backgroundColor: DAY_FILL_COLORS[fill] }}
					/>
					{dayFillLabel(t, fill)}
				</span>
			))}
			<span className="inline-flex items-center gap-1.5">
				<span className="inline-block h-[2px] w-3" style={{ backgroundColor: LATE_COLOR }} />
				{t("dayStrip.legendLate")}
			</span>
			<span className="inline-flex items-center gap-1.5">
				<span className="inline-block h-[2px] w-3" style={{ backgroundColor: EARLY_COLOR }} />
				{t("dayStrip.legendEarly")}
			</span>
		</div>
	);
}
